export type AnalogSignalType = '0-10V' | '2-10V' | '0-20mA' | '4-20mA';

export type AnalogSignalStatus = 'NORMAL' | 'OVER_RANGE' | 'UNDER_RANGE' | 'WIRE_BREAK' | 'INVALID';

export interface AnalogConfig {
  signalType: AnalogSignalType;
  minRaw: number; // Default 0
  maxRaw: number; // Default 27648
  minEng: number;
  maxEng: number;
  clamp: boolean;
}

export interface AnalogResult {
  raw: number;
  eng: number;
  status: AnalogSignalStatus;
}

export const S7_MAX = 27648;
export const S7_WIRE_BREAK = 32767;

export function scaleEngToRaw(eng: number, config: AnalogConfig, simulateWireBreak: boolean = false): AnalogResult {
  if (simulateWireBreak && (config.signalType === '4-20mA' || config.signalType === '2-10V')) {
    return { raw: S7_WIRE_BREAK, eng: Number.NaN, status: 'WIRE_BREAK' };
  }

  if (Number.isNaN(eng) || !Number.isFinite(eng)) {
    return { raw: 0, eng: Number.NaN, status: 'INVALID' };
  }

  const { minRaw, maxRaw, minEng, maxEng, clamp } = config;
  const engRange = maxEng - minEng;
  
  if (engRange === 0) {
    return { raw: 0, eng, status: 'INVALID' };
  }

  let normalized = (eng - minEng) / engRange;
  let status: AnalogSignalStatus = 'NORMAL';

  if (normalized > 1) {
    status = 'OVER_RANGE';
    if (clamp) normalized = 1;
  } else if (normalized < 0) {
    status = 'UNDER_RANGE';
    if (clamp) normalized = 0;
  }

  const rawRange = maxRaw - minRaw;
  const raw = Math.round(minRaw + normalized * rawRange);

  return { raw, eng, status };
}

export function scaleRawToEng(raw: number, config: AnalogConfig): AnalogResult {
  if (Number.isNaN(raw) || !Number.isFinite(raw)) {
    return { raw, eng: Number.NaN, status: 'INVALID' };
  }

  if (raw === S7_WIRE_BREAK && (config.signalType === '4-20mA' || config.signalType === '2-10V')) {
    return { raw, eng: Number.NaN, status: 'WIRE_BREAK' };
  }

  // Very negative raw values in 4-20mA / 2-10V often mean wire break.
  // Standard Siemens: below -4864 is usually considered a wire break for 4-20mA.
  if (raw < -4864 && (config.signalType === '4-20mA' || config.signalType === '2-10V')) {
    return { raw, eng: Number.NaN, status: 'WIRE_BREAK' };
  }

  const { minRaw, maxRaw, minEng, maxEng, clamp } = config;
  const rawRange = maxRaw - minRaw;

  if (rawRange === 0) {
    return { raw, eng: Number.NaN, status: 'INVALID' };
  }

  let normalized = (raw - minRaw) / rawRange;
  let status: AnalogSignalStatus = 'NORMAL';

  if (normalized > 1) {
    status = 'OVER_RANGE';
    if (clamp) normalized = 1;
  } else if (normalized < 0) {
    status = 'UNDER_RANGE';
    if (clamp) normalized = 0;
  }

  const eng = minEng + normalized * (maxEng - minEng);

  return { raw, eng, status };
}

export function getAnalogConfig(props: Record<string, unknown>, defaultMinEng: number, defaultMaxEng: number): AnalogConfig {
  return {
    signalType: (props['signalType'] as AnalogSignalType | undefined) || '0-10V',
    minRaw: Number(props['minRaw'] ?? 0),
    maxRaw: Number(props['maxRaw'] ?? 27648),
    minEng: Number(props['minEng'] ?? defaultMinEng),
    maxEng: Number(props['maxEng'] ?? defaultMaxEng),
    clamp: props['clamp'] === 'true' || props['clamp'] === true
  };
}
